import { useCallback, useRef, useState } from 'react';
import { Upload, X, FileText, Image, Cog, Loader2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import type { UploadProgress } from '@/hooks/useFileUpload';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import {
  validateFile,
  formatFileSize,
  MAX_FILES,
  MAX_TOTAL_SIZE_MB,
  ACCEPTED_EXTENSIONS,
  ALLOWED_FILE_TYPES,
  type FileValidationError,
} from '@/lib/file-upload-constants';

interface FileDropzoneProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  disabled?: boolean;
  uploadProgress?: UploadProgress | null;
}

const CATEGORY_ICONS = {
  document: FileText,
  image: Image,
  cad: Cog,
} as const;

function getFileCategory(file: File) {
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  return ALLOWED_FILE_TYPES.find((t) => t.extensions.includes(ext))?.category ?? 'document';
}

export function FileDropzone({ files, onFilesChange, disabled, uploadProgress }: FileDropzoneProps) {
  const t = useT();
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  const translateValidationError = useCallback(
    (err: FileValidationError): string => {
      if (err.code === 'invalid-type') {
        return t(
          `Dateityp "${err.ext}" ist nicht erlaubt. Erlaubt: ${err.allowed}`,
          `File type "${err.ext}" is not allowed. Allowed: ${err.allowed}`,
        );
      }
      return t(
        `${err.fileName} ist zu groß (max. ${err.maxSizeMB} MB)`,
        `${err.fileName} is too large (max. ${err.maxSizeMB} MB)`,
      );
    },
    [t],
  );

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      setError(null);
      const newFiles = Array.from(incoming);

      if (files.length + newFiles.length > MAX_FILES) {
        setError(
          t(`Maximal ${MAX_FILES} Dateien erlaubt`, `A maximum of ${MAX_FILES} files is allowed`),
        );
        return;
      }

      const errors: string[] = [];
      const valid: File[] = [];

      for (const file of newFiles) {
        const err = validateFile(file);
        if (err) {
          errors.push(translateValidationError(err));
        } else {
          valid.push(file);
        }
      }

      const newTotal = totalSize + valid.reduce((s, f) => s + f.size, 0);
      if (newTotal > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
        setError(
          t(
            `Gesamtgröße darf ${MAX_TOTAL_SIZE_MB} MB nicht überschreiten`,
            `Total size must not exceed ${MAX_TOTAL_SIZE_MB} MB`,
          ),
        );
        return;
      }

      if (errors.length > 0) {
        setError(errors[0]);
        return;
      }

      onFilesChange([...files, ...valid]);
    },
    [files, onFilesChange, totalSize, t, translateValidationError],
  );

  const removeFile = useCallback(
    (index: number) => {
      setError(null);
      onFilesChange(files.filter((_, i) => i !== index));
    },
    [files, onFilesChange]
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items?.length) setIsDragging(true);
  }, []);

  const handleDragOut = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (!disabled && e.dataTransfer.files?.length) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles, disabled]
  );

  const acceptString = ACCEPTED_EXTENSIONS.join(',');

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      <div
        role="button"
        tabIndex={0}
        aria-label={t(
          'Dateien zum Hochladen hierher ziehen oder klicken',
          'Drag files here to upload or click',
        )}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={cn(
          'relative flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 text-center transition-colors cursor-pointer',
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-border hover:border-primary/50 hover:bg-secondary/30',
          disabled && 'pointer-events-none opacity-50'
        )}
      >
        <Upload className="w-8 h-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">
            {t('Dateien auswählen', 'Choose files')}
          </span>{' '}
          {t('oder hierher ziehen', 'or drag them here')}
        </p>
        <p className="text-xs text-muted-foreground">
          {t(
            `PDF, DOCX, PNG, JPG, STEP, STP, DXF, DWG · Max. ${MAX_FILES} Dateien · ${MAX_TOTAL_SIZE_MB} MB gesamt`,
            `PDF, DOCX, PNG, JPG, STEP, STP, DXF, DWG · max. ${MAX_FILES} files · ${MAX_TOTAL_SIZE_MB} MB total`,
          )}
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={acceptString}
          className="sr-only"
          onChange={(e) => {
            if (e.target.files?.length) addFiles(e.target.files);
            e.target.value = '';
          }}
          disabled={disabled}
          aria-hidden
        />
      </div>

      {/* Error */}
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      {/* Upload Progress */}
      {uploadProgress && (
        <div className="space-y-2 animate-fade-in">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span>
              {uploadProgress.currentFile
                ? `${uploadProgress.currentFile} (${uploadProgress.completed}/${uploadProgress.total})`
                : t(
                    `${uploadProgress.completed}/${uploadProgress.total} hochgeladen`,
                    `${uploadProgress.completed}/${uploadProgress.total} uploaded`,
                  )}
            </span>
            <span className="ml-auto font-mono text-xs">{uploadProgress.percent}%</span>
          </div>
          <Progress value={uploadProgress.percent} className="h-2" />
        </div>
      )}

      {/* File list */}
      {files.length > 0 && (
        <ul className="space-y-2" aria-label={t('Ausgewählte Dateien', 'Selected files')}>
          {files.map((file, i) => {
            const cat = getFileCategory(file);
            const Icon = CATEGORY_ICONS[cat];
            return (
              <li
                key={`${file.name}-${file.size}-${i}`}
                className="flex items-center gap-3 rounded-lg border border-border bg-secondary/20 px-3 py-2 text-sm"
              >
                <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="truncate flex-1 min-w-0">{file.name}</span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {formatFileSize(file.size)}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(i);
                  }}
                  className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                  aria-label={t(`${file.name} entfernen`, `Remove ${file.name}`)}
                  disabled={disabled}
                >
                  <X className="w-4 h-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
